import * as React from "react";
import { useState, useEffect } from "react";
import { useErrorStore } from "../store/useErrorStore";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import FormHelperText from "@mui/joy/FormHelperText";
import Input from "@mui/joy/Input";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import Autocomplete from "@mui/joy/Autocomplete";
import Card from "@mui/joy/Card";
import CardActions from "@mui/joy/CardActions";
import CardOverflow from "@mui/joy/CardOverflow";
import Grid from "@mui/joy/Grid";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import CircularProgress from "@mui/joy/CircularProgress";
import states from "../data/states.json";
import cities from "../data/state_cities.json";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import Sidebar from "../components/common/Sidebar";
import Header from "../components/common/Header";
import JoyDatePicker from "../components/common/JoyDatePicker";
import PageBreadcrumbs from "../components/common/PageBreadcrumbs";
import { PersonAddRounded, PersonSearchRounded } from "@mui/icons-material";

dayjs.extend(utc);
dayjs.extend(timezone);

export default function AddDonor() {
  const [donorOptions, setDonorOptions] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [isAddingDonor, setIsAddingDonor] = useState(false);

  const [donorData, setDonorData] = useState({
    id: "",
    name: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    country: "India",
    pincode: "",
    pan: "",
    email: "",
    donorType: "Individual",
    birthdate: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    pan: "",
    birthdate: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isFormEditable, setIsFormEditable] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const { error, setError } = useErrorStore();
  const [originalDonorData, setOriginalDonorData] = useState(donorData);
  const [showDonorForm, setShowDonorForm] = useState(false);
  const hasErrors =
    Object.values(errors).some((err) => err !== "") || !donorData.name || !donorData.phone;

  useEffect(() => {
    if (message || error) {
      const timer = setTimeout(() => {
        setMessage(null);
        setError(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message, error, setError]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let errorMsg = "";
    let validatedValue = value;

    switch (name) {
      case "name":
        if (!/^[A-Za-z0-9 ]*$/.test(value) || value.length > 24) {
          errorMsg = "Max 24 characters. Only letters, numbers, and spaces allowed.";
        }
        validatedValue = value.slice(0, 24);
        break;
      case "phone":
        if (!/^[1-9][0-9]{9}$/.test(value)) {
          errorMsg = "Must be exactly 10 digits and cannot start with 0.";
        }
        validatedValue = value.slice(0, 10);
        break;
      case "email":
        if (value && !/^[\w.-]+@[a-zA-Z\d.-]+\.[a-zA-Z]{2,}$/.test(value)) {
          errorMsg = "Enter a valid email address.";
        }
        break;
      case "address":
        if (!/^[a-zA-Z0-9 ,\-\/]*$/.test(value) || value.length > 100) {
          errorMsg = "Max 100 characters. Only letters, numbers, ',', '-' and '/' allowed.";
        }
        validatedValue = value.slice(0, 100);
        break;
      case "city":
        validatedValue = value.toUpperCase();
        if (!/^[A-Z ]*$/.test(validatedValue) || validatedValue.length > 20) {
          errorMsg = "Max 20 characters. Only uppercase letters and spaces allowed.";
        }
        validatedValue = validatedValue.slice(0, 20);
        break;
      case "pincode":
        if (!/^[0-9]{0,6}$/.test(value)) {
          errorMsg = "Must be exactly 6 digits.";
        }
        validatedValue = value.slice(0, 6);
        break;
      case "pan":
        validatedValue = value.toUpperCase();
        if (value && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(validatedValue)) {
          errorMsg = "PAN must be exactly 10 characters: 5 letters, 4 digits, 1 letter.";
        }
        validatedValue = validatedValue.slice(0, 10);
        break;
      case "birthdate":
        if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
          errorMsg = "Birthdate must be in YYYY-MM-DD format.";
        }
        break;
      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [name]: errorMsg }));
    setDonorData((prev) => ({ ...prev, [name]: validatedValue }));
  };

  const handleSelectChange = (event: any, newValue: string | null, field: string) => {
    setDonorData((prev) => ({ ...prev, [field]: newValue || "" }));
  };

  const handleAddDonor = async () => {
    setIsSubmitting(true);
    setMessage(null);
    setError(null);

    const { id, ...donorPayload } = donorData;
    const formattedBirthdate = donorData.birthdate
      ? dayjs(donorData.birthdate).tz("Asia/Kolkata").format("YYYY-MM-DDTHH:mm:ssZ")
      : null;
    const donorPayloadToSend = {
      ...donorPayload,
      birthdate: formattedBirthdate,
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/donor`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(donorPayloadToSend),
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 401) {
          const redirectUrl = encodeURIComponent(window.location.href);
          window.location.href = `${process.env.REACT_APP_AUTH_WEB_URL}/signin?redirect=${redirectUrl}`;
          return;
        }
        throw new Error("Failed to add donor.");
      }

      const newDonor = await response.json();

      setMessage("Donor added successfully!");
      setDonorData({ ...donorPayload, id: newDonor.donorId });
      setOriginalDonorData(newDonor);
      setIsFormEditable(false);
      setIsEditing(false);
      setShowDonorForm(true);
      setIsAddingDonor(false);
    } catch (err) {
      setError("Error adding donor. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateDonor = async () => {
    setIsSubmitting(true);
    setMessage(null);
    setError(null);

    const formattedBirthdate = donorData.birthdate
      ? dayjs(donorData.birthdate).tz("Asia/Kolkata").format("YYYY-MM-DDTHH:mm:ssZ")
      : null;
    const donorPayloadToSend = {
      ...donorData,
      birthdate: formattedBirthdate,
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/donor/${donorData.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(donorPayloadToSend),
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 401) {
          const redirectUrl = encodeURIComponent(window.location.href);
          window.location.href = `${process.env.REACT_APP_AUTH_WEB_URL}/signin?redirect=${redirectUrl}`;
          return;
        }
        throw new Error("Failed to update donor.");
      }

      setMessage("Donor updated successfully!");
      setOriginalDonorData(donorData);
      setIsFormEditable(false);
      setIsEditing(false);
    } catch (err) {
      setError("Error updating donor. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchDonors = async (query: string) => {
    setIsSearchLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/api/search/donor?q=${query}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          const redirectUrl = encodeURIComponent(window.location.href);
          window.location.href = `${process.env.REACT_APP_AUTH_WEB_URL}/signin?redirect=${redirectUrl}`;
          return;
        }
        throw new Error("Failed to search donors.");
      }

      const data = await response.json();

      if (Array.isArray(data)) {
        data.forEach((donor: any) => {
          if (donor.birthdate === "0001-01-01T00:00:00Z") {
            donor.birthdate = "";
          }
        });
      } else if (data && typeof data === "object" && data.birthdate === "0001-01-01T00:00:00Z") {
        data.birthdate = "";
      }

      setDonorOptions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching donors:", err);
      setDonorOptions([]);
    } finally {
      setIsSearchLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100dvh" }}>
      <Header />
      <Sidebar />
      <Box
        component="main"
        className="MainContent"
        sx={{
          pt: { xs: "calc(12px + var(--Header-height))", md: 3 },
          pb: { xs: 2, sm: 2, md: 3 },
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          gap: 1,
          overflow: "auto",
          bgcolor: "background.body",
        }}
      >
        <Box sx={{ flex: 1, width: "100%" }}>
          <Box
            sx={{
              position: "sticky",
              top: { sm: -100, md: -110 },
              bgcolor: "background.body",
              zIndex: 9995,
            }}
          >
            <Box sx={{ px: { xs: 2, md: 6 } }}>
              <PageBreadcrumbs links={[{ label: "Donor", href: "/donation/donor" }]} current="Add" />
              <Typography level="h2" component="h1" sx={{ mt: 1, mb: 2 }}>
                New Donor
              </Typography>
            </Box>
          </Box>
          <Stack
            spacing={4}
            sx={{
              display: "flex",
              maxWidth: "800px",
              mx: "auto",
              px: { xs: 2, md: 6 },
              py: { xs: 2, md: 3 },
            }}
          >
            <Card>
              <Box sx={{ mb: 1 }}>
                <Typography level="title-md">Donor Details</Typography>
                <Typography level="body-sm">Search for donor profile or add a new one.</Typography>
                <Stack direction="row" spacing={2} sx={{ width: 300, mt: 2, alignItems: "flex-end" }}>
                  <FormControl id="search-donor">
                    <FormLabel>Search donor</FormLabel>
                    <Autocomplete
                      placeholder="Search donor"
                      type="search"
                      freeSolo
                      disableClearable
                      size="sm"
                      options={donorOptions}
                      getOptionLabel={(donor: any) =>
                        donor.name
                          ? donor.phone
                            ? `${donor.name} (${donor.phone})`
                            : donor.email
                              ? `${donor.name} (${donor.email})`
                              : donor.name
                          : "Unknown Donor"
                      }
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      value={null}
                      startDecorator={<PersonSearchRounded />}
                      onInputChange={(event, newValue) => {
                        setSearchTerm(newValue);
                        if (newValue.length >= 3) fetchDonors(newValue);
                      }}
                      filterOptions={(options) => options}
                      onChange={(event, newValue) => {
                        if (newValue) {
                          setMessage(null);
                          setError(null);
                          setErrors({ name: "", phone: "", email: "", address: "", city: "", state: "", pincode: "", pan: "", birthdate: "" });
                          setIsFormEditable(false);
                          setDonorData({
                            id: newValue.id,
                            name: newValue.name,
                            phone: newValue.phone,
                            address: newValue.address,
                            city: newValue.city,
                            state: newValue.state,
                            country: newValue.country,
                            pincode: newValue.pincode,
                            pan: newValue.pan,
                            email: newValue.email,
                            donorType: newValue.donorType,
                            birthdate: newValue.birthdate || "",
                          });
                        }
                      }}
                      endDecorator={isSearchLoading ? (
                        <CircularProgress size="sm" sx={{ bgcolor: "background.surface" }} />
                      ) : null}
                    />
                  </FormControl>
                  <Box sx={{ flexGrow: 1 }}>
                    {(!isAddingDonor || donorData.id || isEditing) && (
                      <Button
                        size="sm"
                        variant="solid"
                        sx={{ width: 130 }}
                        startDecorator={<PersonAddRounded />}
                        onClick={() => {
                          setMessage(null);
                          setError(null);
                          setErrors({ name: "", phone: "", email: "", address: "", city: "", state: "", pincode: "", pan: "", birthdate: "" });
                          setShowDonorForm(true);
                          setIsFormEditable(true);
                          setIsAddingDonor(true);
                          setDonorData({
                            id: "",
                            name: "",
                            phone: "",
                            address: "",
                            city: "",
                            state: "",
                            country: "India",
                            pincode: "",
                            pan: "",
                            email: "",
                            donorType: "Individual",
                            birthdate: "",
                          });
                        }}
                      >
                        Add New
                      </Button>
                    )}
                  </Box>
                </Stack>
              </Box>
              {(showDonorForm || donorData.id) ? (
                <Grid container spacing={2} sx={{ flexGrow: 1 }}>
                  <Grid xs={12} sm={4}>
                    <FormControl sx={{ flexGrow: 2 }} error={!!errors.name}>
                      <FormLabel>Name</FormLabel>
                      <Input
                        size="sm"
                        placeholder="Full name"
                        name="name"
                        value={donorData.name}
                        onChange={handleInputChange}
                        readOnly={!isFormEditable}
                      />
                      {errors.name && <FormHelperText>{errors.name}</FormHelperText>}
                    </FormControl>
                  </Grid>
                  <Grid xs={12} sm={4}>
                    <FormControl sx={{ flexGrow: 1 }} error={!!errors.phone}>
                      <FormLabel>Phone</FormLabel>
                      <Input
                        size="sm"
                        placeholder="e.g., 1234567890"
                        name="phone"
                        value={donorData.phone}
                        onChange={handleInputChange}
                        readOnly={!isFormEditable}
                      />
                      {errors.phone && <FormHelperText>{errors.phone}</FormHelperText>}
                    </FormControl>
                  </Grid>
                  <Grid xs={12} sm={4}>
                    <FormControl sx={{ flexGrow: 1 }} error={!!errors.email}>
                      <FormLabel>Email</FormLabel>
                      <Input
                        size="sm"
                        type="email"
                        placeholder="username@example.com"
                        name="email"
                        value={donorData.email}
                        onChange={handleInputChange}
                        readOnly={!isFormEditable}
                      />
                      {errors.email && <FormHelperText>{errors.email}</FormHelperText>}
                    </FormControl>
                  </Grid>
                  <Grid xs={12} sm={4}>
                    <FormControl error={!!errors.birthdate}>
                      <FormLabel>Birthdate</FormLabel>
                      <JoyDatePicker
                        name="birthdate"
                        value={donorData.birthdate ? dayjs(donorData.birthdate) : null}
                        onChange={(newValue) => {
                          const formattedDate = newValue ? newValue.format("YYYY-MM-DD") : "";
                          setDonorData((prev) => ({ ...prev, birthdate: formattedDate }));
                          setErrors((prev) => ({ ...prev, birthdate: "" }));
                        }}
                        format="YYYY-MM-DD"
                        maxDate={dayjs()}
                        slotProps={{
                          textField: {
                            size: "small",
                            sx: { height: "32px", minHeight: "32px", fontSize: "0.875rem", padding: "5px 10px" },
                          },
                        }}
                        sx={{ height: "32px", minHeight: "32px" }}
                        disabled={!isFormEditable}
                      />
                      {errors.birthdate && <FormHelperText>{errors.birthdate}</FormHelperText>}
                    </FormControl>
                  </Grid>
                  <Grid xs={12} sm={8}>
                    <FormControl sx={{ flexGrow: 1 }} error={!!errors.address}>
                      <FormLabel>Address</FormLabel>
                      <Input
                        size="sm"
                        placeholder="e.g., 62/72 Street, Area"
                        name="address"
                        value={donorData.address}
                        onChange={handleInputChange}
                        readOnly={!isFormEditable}
                      />
                      {errors.address && <FormHelperText>{errors.address}</FormHelperText>}
                    </FormControl>
                  </Grid>
                  <Grid xs={12} sm={4}>
                    <FormControl>
                      <FormLabel>State</FormLabel>
                      <Select
                        size="sm"
                        placeholder="Select"
                        value={donorData.state}
                        onChange={(event, newValue) => handleSelectChange(event, newValue, "state")}
                        disabled={!isFormEditable}
                      >
                        {states.map((state) => (
                          <Option key={state} value={state}>
                            {state}
                          </Option>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid xs={12} sm={4}>
                    <FormControl error={!!errors.city}>
                      <FormLabel>City</FormLabel>
                      <Autocomplete
                        size="sm"
                        freeSolo
                        placeholder="City"
                        value={donorData.city}
                        options={
                          Array.from(new Set(
                            donorData.state && cities[donorData.state as keyof typeof cities]
                              ? cities[donorData.state as keyof typeof cities]
                              : Object.values(cities).flat()
                          ))
                        }
                        onInputChange={(_: React.SyntheticEvent, newInputValue: string) => {
                          setDonorData((prev) => ({ ...prev, city: newInputValue }));
                          if (newInputValue.length > 20 || !/^[A-Z]*$/.test(newInputValue.toUpperCase())) {
                            setErrors((prev) => ({ ...prev, city: "Max 20 characters. Only uppercase letters and spaces allowed." }));
                          } else {
                            setErrors((prev) => ({ ...prev, city: "" }));
                          }
                        }}
                        getOptionLabel={(option: string) => (typeof option === "string" ? option : "")}
                        disabled={!isFormEditable}
                      />
                      {errors.city && <FormHelperText>{errors.city}</FormHelperText>}
                    </FormControl>
                  </Grid>
                  <Grid xs={12} sm={4}>
                    <FormControl error={!!errors.pincode}>
                      <FormLabel>Pincode</FormLabel>
                      <Input
                        size="sm"
                        placeholder="e.g., 000000"
                        name="pincode"
                        value={donorData.pincode}
                        onChange={handleInputChange}
                        readOnly={!isFormEditable}
                      />
                      {errors.pincode && <FormHelperText>{errors.pincode}</FormHelperText>}
                    </FormControl>
                  </Grid>
                  <Grid xs={12} sm={4}>
                    <FormControl error={!!errors.pan}>
                      <FormLabel>PAN</FormLabel>
                      <Input
                        size="sm"
                        placeholder="ABCDE1234F"
                        name="pan"
                        value={donorData.pan}
                        onChange={handleInputChange}
                        readOnly={!isFormEditable}
                      />
                      {errors.pan && <FormHelperText>{errors.pan}</FormHelperText>}
                    </FormControl>
                  </Grid>
                  <Grid xs={12} sm={4}>
                    <FormControl sx={{ flexGrow: 1 }}>
                      <FormLabel>Type</FormLabel>
                      <Select
                        size="sm"
                        placeholder="Select"
                        value={donorData.donorType}
                        onChange={(event, newValue) => handleSelectChange(event, newValue, "donorType")}
                        disabled={!isFormEditable}
                      >
                        <Option value="Individual">Individual</Option>
                        <Option value="Corporate">Corporate</Option>
                        <Option value="Non Profit">Non Profit</Option>
                        <Option value="Other">Other</Option>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              ) : (
                <Typography level="body-sm" sx={{ mt: 2, textAlign: "center" }}>
                  Search or add a donor profile.
                </Typography>
              )}
              {message && (
                <Typography level="body-sm" sx={{ color: "green", mt: 1 }}>
                  {message}
                </Typography>
              )}
              {error && (
                <Typography level="body-sm" sx={{ color: "red", mt: 1 }}>
                  {error}
                </Typography>
              )}
              <CardOverflow>
                {(showDonorForm || donorData.id) && (
                  <CardActions sx={{ alignSelf: "flex-end", pt: 2 }}>
                    {isFormEditable && (
                      <Button
                        size="sm"
                        variant="outlined"
                        color="neutral"
                        onClick={() => {
                          if (!originalDonorData.id) {
                            setShowDonorForm(false);
                            setIsAddingDonor(false);
                            setDonorData({
                              id: "",
                              name: "",
                              phone: "",
                              address: "",
                              city: "",
                              state: "",
                              country: "India",
                              pincode: "",
                              pan: "",
                              email: "",
                              donorType: "Individual",
                              birthdate: "",
                            });
                          } else {
                            setDonorData(originalDonorData);
                            setIsEditing(false);
                            setIsFormEditable(false);
                          }
                        }}
                      >
                        Cancel
                      </Button>
                    )}
                    {donorData.id ? (
                      isEditing ? (
                        <Button size="sm" variant="solid" onClick={handleUpdateDonor} disabled={isSubmitting || hasErrors}>
                          {isSubmitting ? "Updating..." : "Update"}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outlined"
                          onClick={() => {
                            setOriginalDonorData(donorData);
                            setIsEditing(true);
                            setIsFormEditable(true);
                          }}
                        >
                          Edit
                        </Button>
                      )
                    ) : (
                      <Button size="sm" variant="solid" onClick={handleAddDonor} disabled={isSubmitting || hasErrors}>
                        {isSubmitting ? "Adding..." : "Add Donor"}
                      </Button>
                    )}
                  </CardActions>
                )}
              </CardOverflow>
            </Card>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
